// @ts-check
class WishlistIcon extends HTMLElement {
  constructor() {
    super();
    /** @type {EventListener} */
    this._onUpdate = this.updateBubble.bind(this);
  }

  connectedCallback() {
    document.addEventListener('wishlist:update', this._onUpdate);
    this.updateBubble();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:update', this._onUpdate);
  }

  updateBubble() {
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const count = wishlist.length;
    const bubble = /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-bubble]'));
    const countEl = this.querySelector('[data-wishlist-count]');
    if (countEl) countEl.textContent = String(count);
    if (bubble) {
      bubble.style.display = count === 0 ? 'none' : '';
    }
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

class WishlistPage extends HTMLElement {
  constructor() {
    super();
    /** @type {EventListener} */
    this._onUpdate = this.render.bind(this);
  }

  connectedCallback() {
    this.render();
    document.addEventListener('wishlist:update', this._onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:update', this._onUpdate);
  }

  render() {
    const grid = this.querySelector('[data-wishlist-grid]');
    const emptyHide = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-wishlist-empty-hide]'));
    const emptyShow = /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-empty-show]'));

    if (!grid) return;

    /** @type {Array<{handle: string, id?: string, variantId?: string, title: string, image?: string, price?: string, size?: string, color?: string}>} */
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');

    if (wishlist.length === 0) {
      grid.innerHTML = '';
      if (emptyShow) emptyShow.style.display = '';
      emptyHide.forEach(el => { el.style.display = 'none'; });
      return;
    }

    if (emptyShow) emptyShow.style.display = 'none';
    emptyHide.forEach(el => { el.style.display = ''; });

    grid.innerHTML = wishlist.map(item => {
      const safeTitle = (item.title || '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const imgSrc = item.image || '';
      const params = new URLSearchParams();
      if (item.variantId) params.set('variant', item.variantId);
      if (item.color) params.set('color', item.color);
      if (item.size && item.size !== 'Default') params.set('size', item.size);
      const queryString = params.toString();
      const productUrl = `/products/${item.handle}${queryString ? '?' + queryString : ''}`;
      return `
        <article class="wl-card">
          <div class="wl-card__media-wrap">
            <a class="wl-card__media-link" href="${productUrl}">
              ${imgSrc ? `<img class="wl-card__img" src="${imgSrc}" alt="${safeTitle}" loading="lazy">` : ''}
            </a>
            <button
              class="wl-card__remove"
              type="button"
              aria-label="Remove from wishlist"
              aria-pressed="true"
              data-wishlist-button
              data-product-handle="${item.handle}"
              data-product-id="${item.id || ''}"
              data-product-title="${safeTitle}"
              data-product-image="${imgSrc}"
              data-product-price="${item.price || ''}"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" pathLength="100"/>
              </svg>
            </button>
          </div>
          <div class="wl-card__body">
            <a class="wl-card__title" href="${productUrl}">${safeTitle}</a>
            <div class="wl-card__price">${item.price || ''}</div>
            ${(() => {
              const meta = [];
              if (item.color) meta.push(`Color: ${item.color}`);
              if (item.size && item.size !== 'Default') meta.push(`Size: ${item.size}`);
              return meta.length > 0 ? `<div class="wl-card__size">${meta.join(' &bull; ')}</div>` : '';
            })()}
          </div>
        </article>
      `;
    }).join('');
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

/* ── Sync aria-pressed on all wishlist buttons ── */
function syncWishlistButtons() {
  /** @type {Array<{handle: string}>} */
  const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
  const handles = new Set(wishlist.map(i => i.handle));

  const buttons = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-wishlist-button], [data-pf-wishlist], [data-pf-sticky-wishlist]'));
  buttons.forEach(btn => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const inList = handles.has(handle);
    btn.setAttribute('aria-pressed', inList ? 'true' : 'false');
    btn.classList.toggle('is-active', inList);
    btn.classList.toggle('is-saved', inList);
    btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
    if (btn.hasAttribute('title')) {
      btn.setAttribute('title', inList ? 'Remove from wishlist' : 'Add to wishlist');
    }
  });
}

/* ── Global click handler ── */
document.addEventListener('click', e => {
  const target = /** @type {Element | null} */ (e.target);
  if (!target) return;

  // Handle direct remove button
  const removeBtn = /** @type {HTMLElement | null} */ (target.closest('[data-wishlist-remove]'));
  if (removeBtn) {
    e.preventDefault();
    e.stopPropagation();
    const handle = removeBtn.dataset.wishlistRemove;
    if (handle) {
      /** @type {Array<{handle: string}>} */
      let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      wishlist = wishlist.filter(item => item.handle !== handle);
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      document.dispatchEvent(new CustomEvent('wishlist:update'));
    }
    return;
  }

  const btn = /** @type {HTMLElement | null} */ (target.closest('[data-wishlist-button], [data-pf-wishlist], [data-pf-sticky-wishlist]'));
  if (!btn) return;

  if (btn.classList.contains('is-loading')) return;

  e.preventDefault();
  e.stopPropagation();

  const handle = btn.dataset.productHandle;
  if (!handle) return;

  const matchingButtons = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll(`[data-product-handle="${handle}"][data-wishlist-button], [data-product-handle="${handle}"][data-pf-wishlist], [data-product-handle="${handle}"][data-pf-sticky-wishlist]`));
  matchingButtons.forEach(b => b.classList.add('is-loading'));
  btn.classList.add('is-loading');

  setTimeout(() => {
    /** @type {Array<{handle: string, id?: string, variantId?: string, title?: string, image?: string, price?: string, size?: string, color?: string}>} */
    let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const idx = wishlist.findIndex(i => i.handle === handle);

    if (idx > -1) {
      wishlist.splice(idx, 1);
    } else {
      let selectedSize = btn.dataset.productSize || '';
      let selectedColor = btn.dataset.productColor || '';
      let variantId = btn.dataset.productVariantId || '';

      if (!selectedSize) {
        const activePill = /** @type {HTMLElement | null} */ (document.querySelector('[data-pf-size-pill].is-active, [data-pf-size-pill].is-selected, .pf-buybox__size-pill.is-active, .pf-buybox__size-pill.is-selected'));
        if (activePill) {
          selectedSize = (activePill.dataset.pfSizePill || activePill.dataset.size || activePill.dataset.value || activePill.textContent || '').trim();
        }
      }
      if (!selectedColor) {
        const activeSwatch = /** @type {HTMLElement | null} */ (document.querySelector('[data-pf-swatch].is-active, .pf-color-swatch-item.is-active'));
        if (activeSwatch) {
          selectedColor = (activeSwatch.dataset.colorName || '').trim();
        }
      }
      if (!variantId) {
        const variantInput = /** @type {HTMLInputElement | null} */ (document.querySelector('[data-pf-variant-id], input[name="id"]'));
        if (variantInput && variantInput.value) {
          variantId = variantInput.value;
        }
      }

      let itemImage = btn.dataset.productImage || '';
      const primaryImg = /** @type {HTMLImageElement | null} */ (document.querySelector('.pf-gallery__primary img, [data-pf-slide].is-active img'));
      if (primaryImg && primaryImg.src) {
        itemImage = primaryImg.src;
      }

      wishlist.push({
        handle,
        id: btn.dataset.productId || '',
        variantId: variantId,
        title: btn.dataset.productTitle || '',
        image: itemImage,
        price: btn.dataset.productPrice || '',
        size: selectedSize,
        color: selectedColor
      });
    }

    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    matchingButtons.forEach(b => b.classList.remove('is-loading'));
    btn.classList.remove('is-loading');
    document.dispatchEvent(new CustomEvent('wishlist:update'));
  }, 420);
}, true /* capture — ensures it fires before any other handler */);

const win = /** @type {any} */ (window);

/* Helper to clear all wishlist items */
win.clearWishlist = function() {
  localStorage.setItem('wishlist', '[]');
  document.dispatchEvent(new CustomEvent('wishlist:update'));
};

/* Helper to remove single item */
win.removeFromWishlist = function(/** @type {string} */ handle) {
  if (!handle) return;
  /** @type {Array<{handle: string}>} */
  let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
  wishlist = wishlist.filter(item => item.handle !== handle);
  localStorage.setItem('wishlist', JSON.stringify(wishlist));
  document.dispatchEvent(new CustomEvent('wishlist:update'));
};

/* ── Boot ── */
function boot() {
  syncWishlistButtons();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
window.addEventListener('pageshow', boot);
document.addEventListener('shopify:section:load', syncWishlistButtons);
document.addEventListener('wishlist:update', syncWishlistButtons);

/* ── Watch for newly injected buttons (quick-add, infinite scroll) ── */
function initObserver() {
  /** @type {any} */
  let debounceTimer;
  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      for (let i = 0; i < m.addedNodes.length; i++) {
        const node = m.addedNodes[i];
        if (!node || node.nodeType !== 1) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches?.('[data-wishlist-button],[data-pf-wishlist]') ||
            el.querySelector?.('[data-wishlist-button],[data-pf-wishlist]')) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(syncWishlistButtons, 80);
          return;
        }
      }
    }
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(initObserver);
} else {
  window.addEventListener('load', initObserver);
}
